<template>
  <div class="kds-column">
    <div class="kds-column-header" :style="{ borderColor: accentColor }">
      <div class="kds-column-title">
        <v-icon :color="accentColor" small class="mr-2">{{ icon }}</v-icon>
        <span>{{ title }}</span>
      </div>
      <span class="kds-column-count" :style="{ backgroundColor: accentColor }">{{
        tickets.length
      }}</span>
    </div>

    <div class="kds-column-board" ref="board">
      <div
        v-if="pagedTickets.length"
        ref="grid"
        class="kds-col-grid"
        :class="`kds-density-${density}`"
      >
        <template v-for="ticket in pagedTickets">
          <!-- FULL: rich ticket card -->
          <div
            v-if="density === 'full'"
            :key="ticket.id"
            class="kds-col-item kds-ticket"
            :class="{ 'kds-ticket--urgent': ticket.elapsedMinutes >= 20 }"
          >
            <div
              class="kds-ticket-header"
              :style="{ backgroundColor: statusColor(ticket.state) }"
            >
              <div class="kds-ticket-type">
                <v-icon small color="white" class="mr-1">{{ ticket.typeIcon }}</v-icon>
                <span>{{ ticket.typeLabel }}</span>
              </div>
              <div class="kds-ticket-num">#{{ ticket.id }}</div>
            </div>

            <div class="kds-ticket-meta">
              <span class="kds-guest-name">{{ ticket.guestName }}</span>
              <span
                class="kds-elapsed"
                :class="{ 'kds-elapsed--warn': ticket.elapsedMinutes >= 20 }"
              >
                <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                {{ ticket.elapsedMinutes }} min
              </span>
            </div>

            <div class="kds-ticket-items">
              <div v-for="(line, idx) in ticket.items" :key="idx" class="kds-item-line">
                <span class="kds-item-qty">{{ line.quantity }}&times;</span>
                <span class="kds-item-name">{{ line.name }}</span>
              </div>
            </div>

            <div class="kds-ticket-footer">
              <v-menu offset-y top>
                <template v-slot:activator="{ on, attrs }">
                  <button class="kds-status-pill" v-bind="attrs" v-on="on">
                    {{ $t(ticket.state) }}
                    <v-icon x-small color="white" right>mdi-chevron-up</v-icon>
                  </button>
                </template>
                <v-list dense>
                  <v-list-item
                    v-for="status in orderStatuses"
                    :key="status.value"
                    :disabled="ticket.state === status.value"
                    @click="$emit('change-status', ticket, status.value)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon small :color="statusColor(status.value)">{{
                        status.icon
                      }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ $t(status.value) }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>

              <button
                v-if="ticket.nextStatus"
                class="kds-advance-btn"
                :disabled="changingId === ticket.id"
                @click="$emit('advance', ticket)"
              >
                <v-progress-circular
                  v-if="changingId === ticket.id"
                  indeterminate
                  size="18"
                  width="2"
                  color="white"
                />
                <template v-else>
                  <v-icon small color="white" class="mr-1">mdi-check-bold</v-icon>
                  {{ nextStatusLabel(ticket.nextStatus) }}
                </template>
              </button>
            </div>
          </div>

          <!-- COMPACT: single-line summary row -->
          <div
            v-else-if="density === 'compact'"
            :key="ticket.id"
            class="kds-col-item kds-compact-row"
            :class="{ 'kds-compact-row--urgent': ticket.elapsedMinutes >= 20 }"
            @click="$emit('open-details', ticket)"
          >
            <span class="kds-compact-num" :style="{ backgroundColor: accentColor }"
              >#{{ ticket.id }}</span
            >
            <v-icon small color="#9aa0b4" class="mx-2">{{ ticket.typeIcon }}</v-icon>
            <span class="kds-compact-name">{{ ticket.guestShort }}</span>
            <span class="kds-compact-time">
              <v-icon x-small class="mr-1">mdi-clock-outline</v-icon
              >{{ ticket.elapsedMinutes }} min
            </span>
            <button
              v-if="ticket.nextStatus"
              class="kds-compact-advance"
              :disabled="changingId === ticket.id"
              @click.stop="$emit('advance', ticket)"
            >
              <v-progress-circular
                v-if="changingId === ticket.id"
                indeterminate
                size="14"
                width="2"
                color="white"
              />
              <v-icon v-else small color="white">mdi-check-bold</v-icon>
            </button>
          </div>

          <!-- MINIMAL: number-only chip -->
          <button
            v-else
            :key="ticket.id"
            class="kds-col-item kds-mini-chip"
            :class="{ 'kds-mini-chip--urgent': ticket.elapsedMinutes >= 20 }"
            :style="{ borderColor: accentColor }"
            @click="$emit('open-details', ticket)"
          >
            <span class="kds-mini-num">#{{ ticket.id }}</span>
            <span class="kds-mini-time">{{ ticket.elapsedMinutes }}&prime;</span>
          </button>
        </template>
      </div>

      <div v-else class="kds-column-empty">
        <v-icon size="56" color="grey darken-2">mdi-tray-remove</v-icon>
        <p>{{ $t("no_active_orders") }}</p>
      </div>
    </div>

    <div class="kds-column-pagination" v-if="totalPages > 1">
      <button class="kds-page-arrow" @click="prevPage">
        <v-icon color="white" small>mdi-chevron-left</v-icon>
      </button>
      <div class="kds-page-dots">
        <span
          v-for="p in totalPages"
          :key="p"
          class="kds-dot"
          :class="{ active: p - 1 === currentPage }"
          @click="currentPage = p - 1"
        ></span>
      </div>
      <button class="kds-page-arrow" @click="nextPage">
        <v-icon color="white" small>mdi-chevron-right</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { STATUS_COLORS } from "./kdsConstants";

export default {
  name: "KdsColumn",
  props: {
    title: { type: String, required: true },
    icon: { type: String, required: true },
    accentColor: { type: String, required: true },
    tickets: { type: Array, default: () => [] },
    density: { type: String, default: "full" },
    orderStatuses: { type: Array, default: () => [] },
    changingId: { type: [Number, String], default: null },
  },
  data() {
    return {
      currentPage: 0,
      perPage: 8,
      resizeDebounce: null,
      rotationTimer: null,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.tickets.length / this.perPage));
    },
    pagedTickets() {
      const start = this.currentPage * this.perPage;
      return this.tickets.slice(start, start + this.perPage);
    },
  },
  watch: {
    tickets() {
      if (this.currentPage >= this.totalPages) {
        this.currentPage = 0;
      }
      this.$nextTick(() => this.fitToScreen());
    },
    density() {
      this.currentPage = 0;
      this.$nextTick(() => this.fitToScreen());
    },
  },
  mounted() {
    this.$nextTick(() => this.fitToScreen());
    window.addEventListener("resize", this.handleResize);
    this.rotationTimer = setInterval(this.autoAdvancePage, 9000);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    clearTimeout(this.resizeDebounce);
    clearInterval(this.rotationTimer);
  },
  methods: {
    statusColor(status) {
      return STATUS_COLORS[status] || "#757575";
    },
    nextStatusLabel(status) {
      if (status === "accepted") return this.$t("accept_order");
      return this.$t(status);
    },
    prevPage() {
      this.currentPage = this.currentPage === 0 ? this.totalPages - 1 : this.currentPage - 1;
    },
    nextPage() {
      this.currentPage = (this.currentPage + 1) % this.totalPages;
    },
    autoAdvancePage() {
      if (this.totalPages <= 1) return;
      this.currentPage = (this.currentPage + 1) % this.totalPages;
    },
    fitToScreen() {
      const board = this.$refs.board;
      const grid = this.$refs.grid;
      if (!board || !grid) return;

      const sample = grid.querySelector(".kds-col-item");
      if (!sample) return;

      const gridStyle = getComputedStyle(grid);
      const colCount =
        gridStyle.gridTemplateColumns.split(" ").filter(Boolean).length || 1;
      const rowGap = parseFloat(gridStyle.rowGap) || 0;

      const boardRect = board.getBoundingClientRect();
      const cardHeight = sample.offsetHeight + rowGap;
      const rows = Math.max(1, Math.floor(boardRect.height / cardHeight));

      const nextPerPage = Math.max(colCount, colCount * rows);
      if (nextPerPage !== this.perPage) {
        this.perPage = nextPerPage;
      }
    },
    handleResize() {
      clearTimeout(this.resizeDebounce);
      this.resizeDebounce = setTimeout(() => this.fitToScreen(), 200);
    },
  },
};
</script>

<style scoped>
.kds-column {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
}

.kds-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 4px;
  border-bottom: 3px solid;
  color: #fff;
  margin-bottom: 8px;
}

.kds-column-title {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 15px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kds-column-count {
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  border-radius: 12px;
  padding: 2px 10px;
}

.kds-column-board {
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.kds-col-grid {
  display: grid;
  gap: 12px;
  align-content: start;
}

.kds-density-full {
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

.kds-density-compact {
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

.kds-density-minimal {
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
}

/* Full ticket card */
.kds-ticket {
  background: #fdfaf3;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.kds-ticket--urgent {
  box-shadow: 0 0 0 2px #e53935, 0 4px 14px rgba(0, 0, 0, 0.35);
}

.kds-ticket-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
}

.kds-ticket-type {
  display: flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kds-ticket-num {
  font-size: 15px;
}

.kds-ticket-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 0 12px;
  color: #2b2b2b;
}

.kds-guest-name {
  font-weight: 700;
  font-size: 14px;
}

.kds-elapsed {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #6b6b6b;
}

.kds-elapsed--warn {
  color: #e53935;
  font-weight: 700;
}

.kds-ticket-items {
  padding: 8px 12px;
  flex: 1;
}

.kds-item-line {
  display: flex;
  gap: 8px;
  padding: 3px 0;
  border-bottom: 1px dashed #ddd6c4;
  font-size: 14px;
  color: #2b2b2b;
}

.kds-item-line:last-child {
  border-bottom: none;
}

.kds-item-qty {
  font-weight: 700;
  min-width: 26px;
}

.kds-ticket-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px 12px 12px;
}

.kds-status-pill {
  background: #eee9da;
  border: none;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #2b2b2b;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.kds-status-pill .v-icon {
  color: #2b2b2b !important;
}

.kds-advance-btn {
  flex: 1;
  background: #2e7d32;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.kds-advance-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Compact row */
.kds-compact-row {
  background: #1e2338;
  border: 1px solid #2a2f42;
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 8px 10px;
  cursor: pointer;
  gap: 4px;
}

.kds-compact-row--urgent {
  border-color: #e53935;
}

.kds-compact-num {
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  border-radius: 6px;
  padding: 3px 8px;
  white-space: nowrap;
}

.kds-compact-name {
  flex: 1;
  color: #e6e8f2;
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kds-compact-time {
  display: flex;
  align-items: center;
  color: #9aa0b4;
  font-size: 12px;
  white-space: nowrap;
  margin-right: 6px;
}

.kds-compact-advance {
  background: #2e7d32;
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.kds-compact-advance:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Minimal chip */
.kds-mini-chip {
  background: #1e2338;
  border: 2px solid;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 4px;
  cursor: pointer;
  gap: 2px;
}

.kds-mini-chip--urgent {
  box-shadow: 0 0 0 2px #e53935;
}

.kds-mini-num {
  color: #fff;
  font-weight: 700;
  font-size: 18px;
}

.kds-mini-time {
  color: #9aa0b4;
  font-size: 11px;
}

.kds-column-empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c7286;
  gap: 8px;
}

.kds-column-empty p {
  font-size: 13px;
  font-weight: 600;
  margin: 0;
}

.kds-column-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 6px 0 0 0;
}

.kds-page-arrow {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
}

.kds-page-dots {
  display: flex;
  gap: 6px;
}

.kds-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #3b4160;
  cursor: pointer;
}

.kds-dot.active {
  background: #3b5bfd;
}
</style>
